import { TableColumn, TableNode, TableProps } from './Table.types'
import { cn } from '../../utils'
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    RowSelectionState,
    useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { Checkbox } from '../Checkbox'
import { Loader } from '../Loader'
import { Text } from '../Text'
import { HorizontalScrollbar } from './HorizontalScrollbar'
import './Table.css'
import { TableCardView } from './TableCardView'
import { TableHeaderActions } from './TableHeaderActions'
import { TableScrollContainer } from './TableScrollContainer'
import {
    DRAG_HANDLE_COLUMN_ID,
    isControlColumn,
    OptionalSortableRows,
    RowDragHandle,
    SortableTableRow,
    StaticTableRow,
} from './SortableRows'

// Extend TanStack Table's ColumnMeta to include our custom properties
declare module '@tanstack/react-table' {
    // Type parameters must match TanStack's declaration for the merge.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface ColumnMeta<TData, TValue> {
        hasAvatar?: boolean
        isAction?: boolean
    }
}

/**
 * Table component using TanStack Table with Virtual Scrolling
 * Simplified for universal scrolling across all devices
 */

export const Table = forwardRef<HTMLTableElement, TableProps>(
    (
        {
            isSelected = true,
            selectedRows,
            tableColumns = [],
            tableData,
            tableActions,
            tableRightComponent,
            tableLeftComponent,
            isMultiRowSelect = false,
            markAllButtonLabel,
            noDataLabel = 'Trenutno nema podataka za prikazati',
            resetRows,
            selectAllIds = [],
            isLoading,
            isFetching,
            isDetails = false,
            defaultSelectedRows,
            isPopup = false,
            tableClassName,
            headerClassName,
            className,
            headerCellClassName,
            tableWrapperClassName,
            rows,
            t,
            emptyState,
            errorState,
            loadingRows,
            cardViewBreakpoint,
            enableVirtualization,
            onReorder,
            reorderHandleLabel = 'Povuci za promjenu poretka',
        },
        ref
    ) => {
        const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
        const [isAllSelected, setIsAllSelected] = useState(false)
        const [isClicked, setIsClicked] = useState(false)
        const [isCardView, setIsCardView] = useState(false)
        const tableWrapperRef = useRef<HTMLDivElement>(null)
        // Rows can be dragged only in the plain table: virtualized rows are
        // absolutely positioned and the card view has no row elements.
        const isReorderable =
            Boolean(onReorder) && !enableVirtualization && !isCardView

        useEffect(() => {
            if (!cardViewBreakpoint) return
            const mq = window.matchMedia(`(max-width: ${cardViewBreakpoint}px)`)
            const handler = (e: MediaQueryListEvent) => setIsCardView(e.matches)
            setIsCardView(mq.matches)
            mq.addEventListener('change', handler)
            return () => mq.removeEventListener('change', handler)
        }, [cardViewBreakpoint])

        // Stable identity of the data — only changes when actual rows change
        const dataIdentity = useMemo(
            () => tableData?.map((row) => String(row.id)).join(',') ?? '',
            [tableData]
        )

        // Reset selection when data changes (tab switch, filter change, etc.)
        useEffect(() => {
            setRowSelection({})
            setIsAllSelected(false)
        }, [dataIdentity])

        // Reset rows when requested
        useEffect(() => {
            if (resetRows) {
                setRowSelection({})
            }
        }, [resetRows])

        // Map table columns to TanStack Table column definitions
        const columns = useMemo<ColumnDef<TableNode>[]>(() => {
            const cols: ColumnDef<TableNode>[] = []

            if (isReorderable) {
                cols.push({
                    id: DRAG_HANDLE_COLUMN_ID,
                    header: () => <div />,
                    cell: () => <RowDragHandle label={reorderHandleLabel} />,
                    size: 50,
                    minSize: 50,
                    maxSize: 50,
                })
            }

            // Add checkbox column as first column if isSelected is true
            if (isSelected && !isDetails) {
                cols.push({
                    id: 'select',
                    header: () => (
                        <div className="flex items-center justify-center"></div>
                    ),
                    cell: ({ row }) => (
                        <div className="flex items-center justify-center">
                            <Checkbox
                                id={`tableCheckbox-${row.id}`}
                                classNames=""
                                isChecked={row.getIsSelected()}
                                handleChecked={() => row.toggleSelected()}
                            />
                        </div>
                    ),
                    size: 50,
                    minSize: 50,
                    maxSize: 50,
                })
            }

            // Add user-defined columns
            cols.push(
                ...tableColumns.map(
                    (col: TableColumn, index): ColumnDef<TableNode> => ({
                        id: col.label || `column-${index}`,
                        header: col.label,
                        cell: ({ row }) => col.renderCell(row.original),
                        meta: {
                            hasAvatar: col.hasAvatar,
                            isAction: col.isAction,
                        },
                    })
                )
            )

            return cols
            // eslint-disable-next-line react-hooks/exhaustive-deps -- unchanged deps keep current behaviour, see UNIS-105
        }, [
            tableColumns,
            isSelected,
            isDetails,
            isMultiRowSelect,
            isReorderable,
            reorderHandleLabel,
        ])

        const actionColumnIds = useMemo(() => {
            return tableColumns
                .filter((col) => col.isAction)
                .map((col, index) => col.label || `column-${index}`)
        }, [tableColumns])

        const table = useReactTable({
            data: tableData || [],
            columns,
            state: {
                rowSelection,
                columnPinning: {
                    right: actionColumnIds,
                },
            },
            enableRowSelection: !isDetails && isSelected,
            onRowSelectionChange: setRowSelection,
            getCoreRowModel: getCoreRowModel(),
            getRowId: (row) => String(row.id),
        })

        // Always call useVirtualizer (hooks cannot be conditional)
        const rowVirtualizer = useVirtualizer({
            count: table.getRowModel().rows.length,
            getScrollElement: () => tableWrapperRef.current,
            estimateSize: () => 48,
            overscan: 10,
        })

        // Set default selected rows
        useEffect(() => {
            if (defaultSelectedRows && defaultSelectedRows.length > 0) {
                const defaultSelection: RowSelectionState = {}
                defaultSelectedRows.forEach((id) => {
                    if (id) {
                        const rowId = tableData.find(
                            (item) => String(item.id) === String(id)
                        )
                        if (rowId) {
                            defaultSelection[String(rowId.id)] = true
                        }
                    }
                })
                setRowSelection(defaultSelection)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps -- unchanged deps keep current behaviour, see UNIS-105
        }, [])

        // Notify parent of selected rows
        useEffect(() => {
            if (selectedRows) {
                const selectedIds = Object.keys(rowSelection).filter(
                    (key) => rowSelection[key]
                )
                selectedRows(selectedIds)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps -- unchanged deps keep current behaviour, see UNIS-105
        }, [rowSelection])

        // Convert rowSelection state to checkbox state format
        const checkboxState = useMemo(() => {
            const state: Record<string, boolean> = {}
            Object.keys(rowSelection).forEach((id) => {
                if (rowSelection[id]) {
                    state[id] = true
                }
            })
            return state
        }, [rowSelection])

        // Handle select all
        const handleSelectAll = useCallback(() => {
            const allRowIds = tableData.map((item) => String(item.id))
            const allSelected = allRowIds.every((id) => rowSelection[id])

            if (allSelected) {
                setRowSelection({})
                setIsAllSelected(false)
            } else {
                const newSelection: RowSelectionState = {}
                allRowIds.forEach((id) => {
                    newSelection[id] = true
                })
                setRowSelection(newSelection)
            }
        }, [tableData, rowSelection])

        // Select all ids from backend pagination
        useEffect(() => {
            if (isAllSelected && selectAllIds.length > 0) {
                const newSelection: RowSelectionState = {}
                selectAllIds.forEach((id) => {
                    if (id) {
                        newSelection[String(id)] = true
                    }
                })

                const currentKeys = Object.keys(rowSelection).sort().join(',')
                const newKeys = Object.keys(newSelection).sort().join(',')

                if (currentKeys !== newKeys) {
                    setRowSelection(newSelection)
                }
            } else if (!isAllSelected && isClicked) {
                if (Object.keys(rowSelection).length !== 0) {
                    setRowSelection({})
                }
                setIsClicked(false)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps -- unchanged deps keep current behaviour, see UNIS-105
        }, [isAllSelected, selectAllIds, isClicked])

        const RowComponent = isReorderable ? SortableTableRow : StaticTableRow
        const rowIds = table.getRowModel().rows.map((row) => row.id)

        return (
            <div
                className={cn(
                    'flex w-full min-w-0 max-w-full flex-col',
                    className
                )}
            >
                <TableHeaderActions
                    tableData={tableData}
                    tableActions={tableActions}
                    tableRightComponent={tableRightComponent}
                    tableLeftComponent={tableLeftComponent}
                    isDetails={isDetails}
                    checkboxState={checkboxState}
                    setIsClicked={setIsClicked}
                    setIsAllSelected={setIsAllSelected}
                    handleSelectAll={handleSelectAll}
                    isAllSelected={isAllSelected}
                    markAllButtonLabel={
                        isAllSelected
                            ? t('button-actions.deselect-all')
                            : markAllButtonLabel
                    }
                    isMultiRowSelect={isMultiRowSelect}
                    isSelected={isSelected}
                    rows={rows}
                    t={t}
                />

                {isCardView ? (
                    <TableCardView
                        rows={table.getRowModel().rows}
                        columns={columns}
                    />
                ) : (
                    <div className="relative flex w-full min-w-0 max-w-full flex-1 flex-col">
                        <div
                            ref={tableWrapperRef}
                            className={cn(
                                'table-scroll-container table-x-scroll w-full min-w-0 max-w-full',
                                isPopup
                                    ? 'max-h-[70vh] min-h-0 flex-1 overflow-auto'
                                    : 'min-h-0 flex-1 overflow-auto',
                                tableWrapperClassName
                            )}
                            data-table-scroll="true"
                        >
                            <TableScrollContainer disableOverflow={false}>
                                <table
                                    ref={ref}
                                    className={cn(
                                        'w-full',
                                        'min-w-max !border-spacing-x-0 !border-spacing-y-1',
                                        tableClassName
                                    )}
                                    style={{
                                        borderCollapse: 'separate',
                                        borderSpacing: '0 12px',
                                    }}
                                >
                                    <thead
                                        style={
                                            isPopup
                                                ? {
                                                      position:
                                                          'sticky' as const,
                                                      top: 0,
                                                      zIndex: 10,
                                                      backgroundColor:
                                                          'hsl(var(--background-base))',
                                                  }
                                                : undefined
                                        }
                                    >
                                        {table
                                            .getHeaderGroups()
                                            .map((headerGroup) => (
                                                <tr
                                                    key={headerGroup.id}
                                                    className={cn(
                                                        headerClassName,
                                                        'w-full'
                                                    )}
                                                >
                                                    {headerGroup.headers.map(
                                                        (header) => {
                                                            const isCheckboxColumn =
                                                                isControlColumn(
                                                                    header.id
                                                                )
                                                            return (
                                                                <th
                                                                    key={
                                                                        header.id
                                                                    }
                                                                    className={cn(
                                                                        'whitespace-normal border-none py-3 font-sans text-xs font-bold text-color-greyBlue',
                                                                        headerCellClassName,
                                                                        isCheckboxColumn
                                                                            ? 'w-12 px-2'
                                                                            : 'px-2 text-left sm:px-4'
                                                                    )}
                                                                    style={{
                                                                        ...(isCheckboxColumn
                                                                            ? {
                                                                                  width: '50px',
                                                                                  minWidth:
                                                                                      '50px',
                                                                                  maxWidth:
                                                                                      '50px',
                                                                              }
                                                                            : {}),
                                                                        ...(header.column.getIsPinned()
                                                                            ? {
                                                                                  position:
                                                                                      'sticky' as const,
                                                                                  right:
                                                                                      header.column.getIsPinned() ===
                                                                                      'right'
                                                                                          ? 0
                                                                                          : undefined,
                                                                                  zIndex: 2,
                                                                                  backgroundColor:
                                                                                      'hsl(var(--background-base))',
                                                                              }
                                                                            : {}),
                                                                    }}
                                                                >
                                                                    {isCheckboxColumn ? (
                                                                        flexRender(
                                                                            header
                                                                                .column
                                                                                .columnDef
                                                                                .header,
                                                                            header.getContext()
                                                                        )
                                                                    ) : (
                                                                        <Text
                                                                            variant="contained"
                                                                            className="m-0 text-color-greyBlue"
                                                                            text={
                                                                                flexRender(
                                                                                    header
                                                                                        .column
                                                                                        .columnDef
                                                                                        .header,
                                                                                    header.getContext()
                                                                                ) as string
                                                                            }
                                                                        />
                                                                    )}
                                                                </th>
                                                            )
                                                        }
                                                    )}
                                                </tr>
                                            ))}
                                    </thead>
                                    <tbody
                                        style={
                                            enableVirtualization
                                                ? { position: 'relative' }
                                                : undefined
                                        }
                                    >
                                        {table.getRowModel().rows.length ===
                                            0 &&
                                            !isLoading && (
                                                <tr>
                                                    <td
                                                        colSpan={columns.length}
                                                    >
                                                        {errorState ??
                                                            emptyState ?? (
                                                                <div className="flex min-h-[200px] items-center justify-center">
                                                                    <Text
                                                                        variant="contained"
                                                                        color="title"
                                                                        className="text-center text-xs text-background-primary"
                                                                        text={
                                                                            t(
                                                                                'table-states.no-data'
                                                                            ) ||
                                                                            noDataLabel ||
                                                                            'Trenutno nema podataka za prikazati'
                                                                        }
                                                                    />
                                                                </div>
                                                            )}
                                                    </td>
                                                </tr>
                                            )}

                                        {isLoading && loadingRows && (
                                            <>
                                                {Array.from({
                                                    length: loadingRows,
                                                }).map((_, i) => (
                                                    <tr
                                                        key={`skeleton-${i}`}
                                                        className="animate-pulse"
                                                    >
                                                        {columns.map(
                                                            (_col, j) => (
                                                                <td
                                                                    key={j}
                                                                    className="px-2 py-3 sm:px-4"
                                                                >
                                                                    <div className="h-4 rounded bg-border-light" />
                                                                </td>
                                                            )
                                                        )}
                                                    </tr>
                                                ))}
                                            </>
                                        )}

                                        {isLoading && !loadingRows && (
                                            <tr>
                                                <td colSpan={columns.length}>
                                                    <div className="flex items-center justify-center py-8">
                                                        <Loader
                                                            text={t(
                                                                'table-states.loading'
                                                            )}
                                                            variant="contained"
                                                            size="md"
                                                            strokeColor="fill-background-gradient"
                                                            textClassName="!text-background-primary"
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        )}

                                        {!isLoading &&
                                            table.getRowModel().rows.length >
                                                0 &&
                                            (enableVirtualization ? (
                                                <>
                                                    {/* Spacer for total virtual height */}
                                                    <tr
                                                        style={{
                                                            height: `${rowVirtualizer.getTotalSize()}px`,
                                                        }}
                                                    >
                                                        <td
                                                            colSpan={
                                                                columns.length
                                                            }
                                                            style={{
                                                                padding: 0,
                                                                border: 'none',
                                                            }}
                                                        />
                                                    </tr>
                                                    {rowVirtualizer
                                                        .getVirtualItems()
                                                        .map((virtualRow) => {
                                                            const row =
                                                                table.getRowModel()
                                                                    .rows[
                                                                    virtualRow
                                                                        .index
                                                                ]
                                                            const isRowSelected =
                                                                row.getIsSelected()
                                                            return (
                                                                <tr
                                                                    key={row.id}
                                                                    style={{
                                                                        position:
                                                                            'absolute',
                                                                        top: 0,
                                                                        left: 0,
                                                                        width: '100%',
                                                                        transform: `translateY(${virtualRow.start}px)`,
                                                                    }}
                                                                    className={cn(
                                                                        'bg-background-base shadow-[0px_2px_5px_rgba(38,51,77,0.03)]',
                                                                        isSelected &&
                                                                            'hover:bg-work-in-progress transition-colors',
                                                                        isSelected &&
                                                                            isRowSelected &&
                                                                            'bg-inactive/10'
                                                                    )}
                                                                >
                                                                    {row
                                                                        .getVisibleCells()
                                                                        .map(
                                                                            (
                                                                                cell
                                                                            ) => {
                                                                                const isCheckboxColumn =
                                                                                    isControlColumn(
                                                                                        cell
                                                                                            .column
                                                                                            .id
                                                                                    )
                                                                                const isAction =
                                                                                    cell
                                                                                        .column
                                                                                        .columnDef
                                                                                        .meta
                                                                                        ?.isAction

                                                                                return (
                                                                                    <td
                                                                                        key={
                                                                                            cell.id
                                                                                        }
                                                                                        className={cn(
                                                                                            'py-3 font-sans text-xs font-bold text-[hsl(var(--text-secondary))]',
                                                                                            isCheckboxColumn
                                                                                                ? 'w-12 px-2'
                                                                                                : 'px-2 sm:px-4',
                                                                                            !isDetails &&
                                                                                                isSelected &&
                                                                                                !isAction &&
                                                                                                !isCheckboxColumn &&
                                                                                                'cursor-pointer',
                                                                                            isAction &&
                                                                                                'bg-background-base'
                                                                                        )}
                                                                                        style={{
                                                                                            ...(isCheckboxColumn
                                                                                                ? {
                                                                                                      width: '50px',
                                                                                                      minWidth:
                                                                                                          '50px',
                                                                                                      maxWidth:
                                                                                                          '50px',
                                                                                                  }
                                                                                                : {}),
                                                                                            ...(cell.column.getIsPinned()
                                                                                                ? {
                                                                                                      position:
                                                                                                          'sticky' as const,
                                                                                                      right:
                                                                                                          cell.column.getIsPinned() ===
                                                                                                          'right'
                                                                                                              ? 0
                                                                                                              : undefined,
                                                                                                      zIndex: 1,
                                                                                                      backgroundColor:
                                                                                                          'hsl(var(--background-base))',
                                                                                                  }
                                                                                                : {}),
                                                                                        }}
                                                                                        onClick={
                                                                                            !isDetails &&
                                                                                            !isAction &&
                                                                                            !isCheckboxColumn
                                                                                                ? () =>
                                                                                                      row.toggleSelected()
                                                                                                : undefined
                                                                                        }
                                                                                    >
                                                                                        {isCheckboxColumn ? (
                                                                                            flexRender(
                                                                                                cell
                                                                                                    .column
                                                                                                    .columnDef
                                                                                                    .cell,
                                                                                                cell.getContext()
                                                                                            )
                                                                                        ) : (
                                                                                            <div className="break-words">
                                                                                                {flexRender(
                                                                                                    cell
                                                                                                        .column
                                                                                                        .columnDef
                                                                                                        .cell,
                                                                                                    cell.getContext()
                                                                                                )}
                                                                                            </div>
                                                                                        )}
                                                                                    </td>
                                                                                )
                                                                            }
                                                                        )}
                                                                </tr>
                                                            )
                                                        })}
                                                </>
                                            ) : (
                                                <OptionalSortableRows
                                                    ids={rowIds}
                                                    onReorder={
                                                        isReorderable
                                                            ? onReorder
                                                            : undefined
                                                    }
                                                >
                                                    {table
                                                        .getRowModel()
                                                        .rows.map((row) => {
                                                            const isRowSelected =
                                                                row.getIsSelected()
                                                            return (
                                                                <RowComponent
                                                                    key={row.id}
                                                                    id={row.id}
                                                                    className={cn(
                                                                        'bg-background-base shadow-[0px_2px_5px_rgba(38,51,77,0.03)]',
                                                                        isSelected &&
                                                                            'hover:bg-work-in-progress transition-colors',
                                                                        isSelected &&
                                                                            isRowSelected &&
                                                                            'bg-inactive/10'
                                                                    )}
                                                                >
                                                                    {row
                                                                        .getVisibleCells()
                                                                        .map(
                                                                            (
                                                                                cell,
                                                                                cellIndex
                                                                            ) => {
                                                                                const isCheckboxColumn =
                                                                                    isControlColumn(
                                                                                        cell
                                                                                            .column
                                                                                            .id
                                                                                    )
                                                                                const isAction =
                                                                                    cell
                                                                                        .column
                                                                                        .columnDef
                                                                                        .meta
                                                                                        ?.isAction
                                                                                const isFirst =
                                                                                    cellIndex ===
                                                                                    0
                                                                                const isLast =
                                                                                    cellIndex ===
                                                                                    row.getVisibleCells()
                                                                                        .length -
                                                                                        1

                                                                                return (
                                                                                    <td
                                                                                        key={
                                                                                            cell.id
                                                                                        }
                                                                                        className={cn(
                                                                                            'py-3 font-sans text-xs font-bold text-[hsl(var(--text-secondary))]',
                                                                                            isCheckboxColumn
                                                                                                ? 'w-12 px-2'
                                                                                                : 'px-2 sm:px-4',
                                                                                            !isDetails &&
                                                                                                isSelected &&
                                                                                                !isAction &&
                                                                                                !isCheckboxColumn &&
                                                                                                'cursor-pointer',
                                                                                            isAction &&
                                                                                                'bg-background-base',
                                                                                            isFirst &&
                                                                                                'rounded-l-[5px]',
                                                                                            isLast &&
                                                                                                'rounded-r-[5px]'
                                                                                        )}
                                                                                        style={{
                                                                                            ...(isCheckboxColumn
                                                                                                ? {
                                                                                                      width: '50px',
                                                                                                      minWidth:
                                                                                                          '50px',
                                                                                                      maxWidth:
                                                                                                          '50px',
                                                                                                  }
                                                                                                : {}),
                                                                                            ...(cell.column.getIsPinned()
                                                                                                ? {
                                                                                                      position:
                                                                                                          'sticky' as const,
                                                                                                      right:
                                                                                                          cell.column.getIsPinned() ===
                                                                                                          'right'
                                                                                                              ? 0
                                                                                                              : undefined,
                                                                                                      zIndex: 1,
                                                                                                      backgroundColor:
                                                                                                          'hsl(var(--background-base))',
                                                                                                  }
                                                                                                : {}),
                                                                                        }}
                                                                                        onClick={
                                                                                            !isDetails &&
                                                                                            !isAction &&
                                                                                            !isCheckboxColumn
                                                                                                ? () =>
                                                                                                      row.toggleSelected()
                                                                                                : undefined
                                                                                        }
                                                                                    >
                                                                                        {isCheckboxColumn ? (
                                                                                            flexRender(
                                                                                                cell
                                                                                                    .column
                                                                                                    .columnDef
                                                                                                    .cell,
                                                                                                cell.getContext()
                                                                                            )
                                                                                        ) : (
                                                                                            <div className="break-words">
                                                                                                {flexRender(
                                                                                                    cell
                                                                                                        .column
                                                                                                        .columnDef
                                                                                                        .cell,
                                                                                                    cell.getContext()
                                                                                                )}
                                                                                            </div>
                                                                                        )}
                                                                                    </td>
                                                                                )
                                                                            }
                                                                        )}
                                                                </RowComponent>
                                                            )
                                                        })}
                                                </OptionalSortableRows>
                                            ))}

                                        {isFetching && (
                                            <tr>
                                                <td colSpan={columns.length}>
                                                    <div className="flex items-center justify-center py-4">
                                                        <Loader
                                                            text={t(
                                                                'table-states.loading'
                                                            )}
                                                            variant="contained"
                                                            size="md"
                                                            strokeColor="fill-background-gradient"
                                                            textClassName="!text-background-primary"
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </TableScrollContainer>
                        </div>
                        <HorizontalScrollbar scrollRef={tableWrapperRef} />
                    </div>
                )}
            </div>
        )
    }
)
Table.displayName = 'Table'
